import React, { useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { Select as AntSelect, SelectProps } from "antd";

import { changeLanguage, getEnvironment, getLanguageFromLocalStorage, useNavigate, useUserContext } from "utils";

import {
  GET_FULL_POPUP,
  GET_PRACTICES_CROPS,
  GET_PRACTICES_FERTILIZERS_APPLICATIONS,
  GET_PRACTICES_FERTILIZERS_MINERAL,
  GET_PRACTICES_FERTILIZERS_ORGANIC,
  GET_PRACTICES_PLANT_PPROTECTION,
  GET_PRACTICES_SEEDINGS,
  GET_PRACTICES_TILLAGES,
  LANGUAGES,
  LANGUAGE_CODE,
} from "consts";

import { Icon, Option } from "components";

const queryKeysToInvalidate = [
  GET_FULL_POPUP,
  GET_PRACTICES_CROPS,
  GET_PRACTICES_TILLAGES,
  GET_PRACTICES_SEEDINGS,
  GET_PRACTICES_PLANT_PPROTECTION,
  GET_PRACTICES_FERTILIZERS_MINERAL,
  GET_PRACTICES_FERTILIZERS_ORGANIC,
  GET_PRACTICES_FERTILIZERS_APPLICATIONS,
];

export const SelectLanguage = ({ placement, skipRefresh }: { placement?: SelectProps["placement"]; skipRefresh?: boolean }) => {
  const navigate = useNavigate();
  const env = getEnvironment();
  const { user } = useUserContext();
  const queryClient = useQueryClient();
  const [isChangingTheLanguage, setIsChangingTheLanguage] = useState<boolean>(false);

  const [language, setLanguage] = useState<LANGUAGE_CODE>(getLanguageFromLocalStorage());

  const handleLanguageChange = (selectedLanguageCode: LANGUAGE_CODE) => {
    const currentLanguageCode = getLanguageFromLocalStorage();
    if (selectedLanguageCode === currentLanguageCode) return;

    setIsChangingTheLanguage(true);
    changeLanguage(selectedLanguageCode || LANGUAGE_CODE.GERMAN, user).then(() => {
      queryKeysToInvalidate.forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
      setLanguage(selectedLanguageCode);
      setIsChangingTheLanguage(false);
      if (!skipRefresh) {
        navigate(0);
      }
    });
  };

  const allLanguageOptions = () => (
    <>
      {LANGUAGES.map((language) => {
        if (language.development && language.code === LANGUAGE_CODE.ZULU && env.staging) return null;
        if ((language.development && (env.local || env.development || env.staging)) || !language.development) {
          return (
            <Option data-testid={`language-option-${language.code}`} key={language.code} label={language.code} value={language.code}>
              {language.language}
            </Option>
          );
        }
        return null;
      })}
    </>
  );

  return (
    <div className="language-select">
      <Icon iconName="tabler/world-longitude" className={`${isChangingTheLanguage && "loading"}`} />
      <AntSelect
        data-testid="language-select"
        optionLabelProp="label"
        placement={placement}
        variant="borderless"
        value={language}
        onChange={handleLanguageChange}
        menuItemSelectedIcon={<Icon iconName="tabler/circle-check" />}
        showAction={["focus"]}
        suffixIcon={false}
        classNames={{ popup: { root: "language-select-dropdown" } }}
        size="small"
        disabled={isChangingTheLanguage}
      >
        {allLanguageOptions()}
      </AntSelect>
    </div>
  );
};
